import React, { useState, useEffect } from 'react';
import { db, storage, auth } from '../firebase';
import { 
  collection, getDocs, addDoc, doc, updateDoc, getDoc,
  serverTimestamp
} from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { useNavigate, useParams, useLocation } from 'react-router-dom';

// Types de ressource qui passent par un upload de fichier
const FILE_TYPES = ['Document PDF', 'Document', 'Image', 'Vidéo', 'Musique', 'Autre'];

// Extensions acceptées pour le type "Document" (bureautique).
// Validation par extension : les navigateurs (surtout sous Windows) renvoient
// des MIME types peu fiables pour Excel/CSV.
const OFFICE_EXTENSIONS = [
  '.xls', '.xlsx', '.csv', '.ods',
  '.doc', '.docx', '.odt', '.rtf', '.txt',
  '.ppt', '.pptx', '.odp'
];

const getExtension = (fileName) => {
  const idx = fileName.lastIndexOf('.');
  return idx >= 0 ? fileName.slice(idx).toLowerCase() : '';
};

// Supprime un ancien fichier Storage (remplacement en mode édition)
const deleteStorageFile = async (url) => {
  if (!url || !url.includes('firebasestorage.googleapis.com')) return;
  try {
    await deleteObject(ref(storage, url));
  } catch (err) {
    if (err?.code !== 'storage/object-not-found') {
      console.warn('Ancien fichier Storage non supprimé :', err);
    }
  }
};

function ResourceForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const [courses, setCourses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [folders, setFolders] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    type: 'Document PDF',
    target: 'course', // 'course' | 'folder'
    courseId: '',
    categoryId: '',
    folderId: '',
    filePath: '',
    fileName: '',
    link: '',
    dateAdded: new Date().toISOString()
  });
  const [originalFilePath, setOriginalFilePath] = useState('');
  const [file, setFile] = useState(null);
  const [files, setFiles] = useState([]); // sélection multiple, mode création uniquement
  const [uploadProgress, setUploadProgress] = useState(0);
  const [bulkProgress, setBulkProgress] = useState({ done: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Taille maximale des fichiers : 50 Mo (= limite des règles Storage)
  const MAX_FILE_SIZE = 50 * 1024 * 1024;

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Récupérer les cours
        const coursesSnapshot = await getDocs(collection(db, 'dance_courses'));
        const coursesData = coursesSnapshot.docs.map(doc => ({
          id: doc.id,
          title: doc.data().title
        }));
        
        // Récupérer les catégories depuis resource_categories
        const categoriesSnapshot = await getDocs(collection(db, 'resource_categories'));
        const categoriesData = categoriesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        // Récupérer les dossiers élèves/général
        const foldersSnapshot = await getDocs(collection(db, 'resource_folders'));
        const foldersData = foldersSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
    
        setCourses(coursesData);
        setCategories(categoriesData);
        setFolders(foldersData);
        
        if (id) {
          const resourceDoc = await getDoc(doc(db, 'resources', id));
          if (resourceDoc.exists()) {
            const data = resourceDoc.data();
            setOriginalFilePath(data.filePath || '');
            setFormData(prev => ({
              ...prev,
              ...data,
              target: data.folderId ? 'folder' : 'course',
              // Convertir le timestamp Firestore en chaîne ISO si nécessaire
              dateAdded: data.dateAdded?.toDate ? data.dateAdded.toDate().toISOString() : data.dateAdded
            }));
          }
        } else {
          const queryParams = new URLSearchParams(location.search);
          const courseId = queryParams.get('courseId');
          const categoryId = queryParams.get('categoryId');
          const folderId = queryParams.get('folderId');
          
          if (folderId) {
            setFormData(prev => ({ ...prev, target: 'folder', folderId }));
          } else if (courseId && categoryId) {
            setFormData(prev => ({
              ...prev,
              target: 'course',
              courseId,
              categoryId
            }));
          }
        }
        
        setLoading(false);
      } catch (err) {
        console.error("Erreur lors du chargement:", err);
        setError("Erreur lors du chargement des données");
        setLoading(false);
      }
    };
    
    fetchData();
  }, [id, location.search]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      // Réinitialiser la catégorie si on change de cours
      if (name === 'courseId') {
        return { ...prev, [name]: value, categoryId: '' };
      }
      // Réinitialiser le lien si on passe d'un type lien à un autre type
      if (name === 'type' && value !== 'Lien externe') {
        return { ...prev, [name]: value, link: '' };
      }
      // Réinitialiser les champs liés à l'autre cible quand on change de cible
      if (name === 'target') {
        return {
          ...prev,
          target: value,
          courseId: value === 'course' ? prev.courseId : '',
          categoryId: value === 'course' ? prev.categoryId : '',
          folderId: value === 'folder' ? prev.folderId : ''
        };
      }
      return { ...prev, [name]: value };
    });
    // Changer de type invalide la sélection de fichiers en cours
    if (name === 'type') {
      setFiles([]);
      setFile(null);
    }
  };

  const validateFile = (selectedFile) => {
    if (selectedFile.size > MAX_FILE_SIZE) {
      return `${selectedFile.name} : trop volumineux (max ${MAX_FILE_SIZE / 1024 / 1024} Mo)`;
    }

    const resourceType = formData.type;

    // Bureautique : validation par extension
    if (resourceType === 'Document') {
      const ext = getExtension(selectedFile.name);
      if (!OFFICE_EXTENSIONS.includes(ext)) {
        return `${selectedFile.name} : type non valide (formats acceptés : ${OFFICE_EXTENSIONS.join(', ')})`;
      }
      return null;
    }

    let validMimeTypes = [];
    let allowedExtensions = [];

    switch (resourceType) {
      case 'Document PDF':
        validMimeTypes = ['application/pdf'];
        allowedExtensions = ['.pdf'];
        break;
      case 'Image':
        validMimeTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
        allowedExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp'];
        break;
      case 'Vidéo':
        validMimeTypes = ['video/mp4', 'video/quicktime'];
        allowedExtensions = ['.mp4', '.mov'];
        break;
      case 'Musique':
        validMimeTypes = ['audio/mpeg', 'audio/wav'];
        allowedExtensions = ['.mp3', '.wav'];
        break;
      default:
        // 'Autre' : tout format accepté (seule la taille est contrôlée)
        validMimeTypes = [];
    }

    if (validMimeTypes.length > 0 && !validMimeTypes.includes(selectedFile.type)) {
      return `${selectedFile.name} : type non valide (formats acceptés : ${allowedExtensions.join(', ')})`;
    }

    return null;
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (selectedFiles.length === 0) {
      setFile(null);
      setFiles([]);
      return;
    }

    const rejected = [];
    const valid = [];
    selectedFiles.forEach(f => {
      const err = validateFile(f);
      if (err) rejected.push(err);
      else valid.push(f);
    });

    if (rejected.length > 0) {
      setError(rejected.join(' — '));
    } else {
      setError('');
    }

    setFiles(valid);
    setFile(valid[0] || null);
  };

  const removeSelectedFile = (index) => {
    setFiles(prev => {
      const next = prev.filter((_, i) => i !== index);
      setFile(next[0] || null);
      return next;
    });
  };

  const uploadOne = (storagePath, fileToUpload) => {
    const storageRef = ref(storage, storagePath);
    const uploadTask = uploadBytesResumable(storageRef, fileToUpload);
    return new Promise((resolve, reject) => {
      uploadTask.on('state_changed',
        (snapshot) => {
          setUploadProgress((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
        },
        reject,
        async () => {
          try {
            resolve(await getDownloadURL(uploadTask.snapshot.ref));
          } catch (err) {
            reject(err);
          }
        }
      );
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Validation de la cible
      if (formData.target === 'course' && (!formData.courseId || !formData.categoryId)) {
        throw new Error('Veuillez sélectionner un cours et une catégorie.');
      }
      if (formData.target === 'folder' && !formData.folderId) {
        throw new Error('Veuillez sélectionner un dossier.');
      }

      // Validation du contenu
      if (formData.type === 'Lien externe') {
        if (!formData.link) {
          throw new Error('Veuillez saisir un lien pour le type de ressource "Lien externe".');
        }
      } else if (!id && files.length === 0) {
        // Création : au moins un fichier requis pour les types fichier
        throw new Error(`Veuillez sélectionner au moins un fichier pour le type de ressource ${formData.type}.`);
      } else if (id && !file && !formData.filePath) {
        // Édition : fichier existant ou nouveau requis
        throw new Error(`Veuillez sélectionner un fichier pour le type de ressource ${formData.type}.`);
      }

      const folderPath = formData.target === 'folder' ? formData.folderId : 'cours';
      const baseData = {
        description: formData.description,
        type: formData.type,
        courseId: formData.target === 'course' ? formData.courseId : null,
        categoryId: formData.target === 'course' ? formData.categoryId : null,
        folderId: formData.target === 'folder' ? formData.folderId : null,
        link: formData.type === 'Lien externe' ? formData.link : ''
      };
      // Champs posés uniquement à la création. En modification on ne les
      // touche plus : avant, modifier un envoi d'élève le réattribuait à
      // l'admin (l'élève ne le voyait plus comme le sien) et le publiait.
      const creationData = {
        uploadedBy: auth.currentUser?.uid || null,
        uploadedByRole: 'admin',
        status: 'approved'
      };

      // ── Mode création avec plusieurs fichiers : un doc par fichier ──────
      if (!id && files.length > 0 && formData.type !== 'Lien externe') {
        setBulkProgress({ done: 0, total: files.length });

        for (let i = 0; i < files.length; i++) {
          const currentFile = files[i];
          const downloadURL = await uploadOne(
            `resources/${folderPath}/${Date.now()}_${i}_${currentFile.name}`,
            currentFile
          );

          // Titre : celui saisi si un seul fichier, sinon "Titre (n)" ou le
          // nom du fichier si aucun titre commun n'a été renseigné.
          const fileBaseName = currentFile.name.replace(/\.[^/.]+$/, '');
          const title = formData.title.trim()
            ? (files.length > 1 ? `${formData.title.trim()} (${i + 1})` : formData.title.trim())
            : fileBaseName;

          await addDoc(collection(db, 'resources'), {
            ...baseData,
            ...creationData,
            title,
            filePath: downloadURL,
            fileName: currentFile.name,
            dateAdded: serverTimestamp()
          });

          setBulkProgress({ done: i + 1, total: files.length });
          setUploadProgress(0);
        }

        navigate('/resources');
        return;
      }

      // ── Mode édition, ou création avec un seul fichier / lien externe ──
      let fileUrl = formData.type === 'Lien externe' ? '' : formData.filePath;
      let fileName = formData.type === 'Lien externe' ? '' : (formData.fileName || '');

      if (file && formData.type !== 'Lien externe') {
        fileUrl = await uploadOne(`resources/${folderPath}/${Date.now()}_${file.name}`, file);
        fileName = file.name;
      }

      const dataToSave = {
        ...baseData,
        title: formData.title,
        filePath: fileUrl,
        fileName
      };

      if (id) {
        // dateAdded n'est plus réécrit : avant, il était renvoyé sous forme
        // de texte (ISO) au lieu d'un Timestamp, ce qui cassait l'affichage
        // de la ressource dans l'app mobile.
        await updateDoc(doc(db, 'resources', id), dataToSave);
        // Fichier remplacé (ou passage en lien externe) : on efface l'ancien
        if (originalFilePath && originalFilePath !== fileUrl) {
          await deleteStorageFile(originalFilePath);
        }
      } else {
        await addDoc(collection(db, 'resources'), {
          ...dataToSave,
          ...creationData,
          dateAdded: serverTimestamp()
        });
      }

      navigate('/resources');
      
    } catch (err) {
      console.error("Erreur lors de l'enregistrement:", err);
      setError(err.message || "Une erreur est survenue lors de l'enregistrement. Veuillez réessayer.");
      setLoading(false);
    }
  };

  // Catégories partagées (courseIds) + anciennes catégories (courseId seul)
  const getCategoriesForCourse = (courseId) => {
    return categories.filter(category => {
      const ids = Array.isArray(category.courseIds) && category.courseIds.length > 0
        ? category.courseIds
        : (category.courseId ? [category.courseId] : []);
      return ids.includes(courseId);
    });
  };

  const folderLabel = (folder) => {
    if (folder.type === 'general') return `${folder.name} (Général)`;
    if (folder.type === 'courses') {
      const count = (folder.courseIds || []).length;
      return `${folder.name} (${count} cours)`;
    }
    const count = (folder.studentIds || []).length;
    return `${folder.name} (${count} élève${count !== 1 ? 's' : ''})`;
  };

  const fileInputLabel = () => {
    switch (formData.type) {
      case 'Document PDF': return 'Fichier(s) PDF';
      case 'Document': return 'Document(s) (Excel, Word, PowerPoint…)';
      case 'Image': return 'Image(s)';
      case 'Vidéo': return 'Fichier(s) Vidéo';
      case 'Musique': return 'Fichier(s) Audio';
      default: return 'Fichier(s)';
    }
  };

  const fileInputAccept = () => {
    switch (formData.type) {
      case 'Document PDF': return '.pdf';
      case 'Document': return OFFICE_EXTENSIONS.join(',');
      case 'Image': return 'image/*';
      case 'Vidéo': return 'video/*';
      case 'Musique': return 'audio/*';
      default: return undefined; // 'Autre' : tout fichier
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Chargement en cours...</p>
      </div>
    );
  }

  return (
    <div className="resource-form-container">
      <h1>{id ? 'Modifier' : 'Ajouter'} une ressource</h1>
      
      {error && (
        <div className="error-message">
          <p>{error}</p>
          {error.includes('taille') && (
            <p>La taille maximale des fichiers est de {MAX_FILE_SIZE / 1024 / 1024} Mo.</p>
          )}
          {error.includes('type') && (
            <p>Veuillez vérifier le format du fichier.</p>
          )}
        </div>
      )}
      
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label>Titre {!id && files.length > 1 ? '(optionnel si plusieurs fichiers)' : '*'}</label>
          <input 
            type="text" 
            name="title" 
            value={formData.title} 
            onChange={handleChange} 
            placeholder={!id && files.length > 1 ? 'Laissez vide pour utiliser le nom de chaque fichier' : ''}
            required={id || files.length <= 1}
          />
          {!id && files.length > 1 && (
            <small>
              Avec {files.length} fichiers sélectionnés, ce titre sera numéroté automatiquement
              (ex: "{formData.title || 'Sans titre'} (1)", "(2)"...), ou le nom de fichier sera utilisé si laissé vide.
            </small>
          )}
        </div>
        
        <div className="form-group">
          <label>Description</label>
          <textarea 
            name="description" 
            value={formData.description} 
            onChange={handleChange} 
            rows="4"
          />
        </div>
        
        <div className="form-group">
          <label>Type de ressource *</label>
          <select name="type" value={formData.type} onChange={handleChange} required>
            <option value="Document PDF">Document PDF</option>
            <option value="Document">Document (Excel, Word, PowerPoint, CSV…)</option>
            <option value="Image">Image</option>
            <option value="Vidéo">Vidéo</option>
            <option value="Musique">Musique</option>
            <option value="Lien externe">Lien externe</option>
            <option value="Autre">Autre (tout type de fichier)</option>
          </select>
        </div>

        <div className="form-group">
          <label>Destination de la ressource *</label>
          <select name="target" value={formData.target} onChange={handleChange} required>
            <option value="course">Un cours (catégorie existante)</option>
            <option value="folder">Un dossier (élèves / cours / général)</option>
          </select>
        </div>
        
        {formData.target === 'course' && (
          <>
            <div className="form-group">
              <label>Cours associé *</label>
              <select 
                name="courseId" 
                value={formData.courseId} 
                onChange={handleChange}
                required
              >
                <option value="">Sélectionner un cours</option>
                {courses.map(course => (
                  <option key={course.id} value={course.id}>
                    {course.title}
                  </option>
                ))}
              </select>
            </div>
            
            {formData.courseId && (
              <div className="form-group">
                <label>Catégorie *</label>
                <select 
                  name="categoryId" 
                  value={formData.categoryId} 
                  onChange={handleChange}
                  required
                >
                  <option value="">Sélectionner une catégorie</option>
                  {getCategoriesForCourse(formData.courseId).map(category => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </>
        )}

        {formData.target === 'folder' && (
          <div className="form-group">
            <label>Dossier *</label>
            <select
              name="folderId"
              value={formData.folderId}
              onChange={handleChange}
              required
            >
              <option value="">Sélectionner un dossier</option>
              {folders.map(folder => (
                <option key={folder.id} value={folder.id}>
                  {folderLabel(folder)}
                </option>
              ))}
            </select>
            {folders.length === 0 && (
              <small>Aucun dossier créé pour l'instant. Créez-en un depuis "Gérer les dossiers".</small>
            )}
          </div>
        )}
        
        {/* Champ pour les fichiers */}
        {FILE_TYPES.includes(formData.type) && (
          <div className="form-group">
            <label>
              {fileInputLabel()} {!id ? '(plusieurs possibles) *' : '*'}
            </label>
            {formData.filePath && !file && (
              <div className="existing-file">
                <p>
                  Fichier actuel{formData.fileName ? ` (${formData.fileName})` : ''} :{' '}
                  <a href={formData.filePath} target="_blank" rel="noreferrer">Télécharger</a>
                </p>
              </div>
            )}
            <input 
              key={formData.type}
              type="file" 
              multiple={!id}
              accept={fileInputAccept()}
              onChange={handleFileChange}
              required={!id && !formData.filePath && files.length === 0}
            />

            {!id && files.length > 0 && (
              <ul style={{ marginTop: 10, paddingLeft: 0, listStyle: 'none' }}>
                {files.map((f, index) => (
                  <li key={`${f.name}-${index}`} style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '6px 10px', background: '#f5f5f5', borderRadius: 6, marginBottom: 4
                  }}>
                    <span style={{ fontSize: 13 }}>{f.name} ({(f.size / 1024 / 1024).toFixed(1)} Mo)</span>
                    <button
                      type="button"
                      onClick={() => removeSelectedFile(index)}
                      style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 13 }}
                    >
                      Retirer
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {bulkProgress.total > 0 && (
              <div className="upload-progress">
                <progress value={bulkProgress.done} max={bulkProgress.total}></progress>
                <span>{bulkProgress.done}/{bulkProgress.total} fichiers envoyés</span>
              </div>
            )}
            {uploadProgress > 0 && uploadProgress < 100 && (
              <div className="upload-progress">
                <progress value={uploadProgress} max="100"></progress>
                <span>{Math.round(uploadProgress)}%</span>
              </div>
            )}
            <small>
              {formData.type === 'Document PDF' && 'Formats acceptés: PDF (max 50 Mo par fichier)'}
              {formData.type === 'Document' && 'Formats acceptés: Excel (XLS, XLSX, CSV), Word (DOC, DOCX, RTF, TXT), PowerPoint (PPT, PPTX), LibreOffice (ODS, ODT, ODP) — max 50 Mo par fichier'}
              {formData.type === 'Image' && 'Formats acceptés: JPG, PNG, GIF (max 50 Mo par fichier)'}
              {formData.type === 'Vidéo' && 'Formats acceptés: MP4, MOV (max 50 Mo par fichier)'}
              {formData.type === 'Musique' && 'Formats acceptés: MP3, WAV (max 50 Mo par fichier)'}
              {formData.type === 'Autre' && 'Tout type de fichier (max 50 Mo par fichier)'}
            </small>
          </div>
        )}
        
        {/* Champ pour les liens */}
        {formData.type === 'Lien externe' && (
          <div className="form-group">
            <label>URL *</label>
            <input 
              type="url" 
              name="link" 
              value={formData.link} 
              onChange={handleChange} 
              placeholder="https://example.com" 
              required
            />
          </div>
        )}
        
        <div className="form-actions">
          <button 
            type="button" 
            className="cancel-button"
            onClick={() => navigate('/resources')}
            disabled={loading}
          >
            Annuler
          </button>
          <button 
            type="submit" 
            className="submit-button"
            disabled={loading}
          >
            {loading
              ? (bulkProgress.total > 0
                  ? `Envoi ${bulkProgress.done}/${bulkProgress.total}...`
                  : (id ? 'Mise à jour...' : 'Ajout en cours...'))
              : (id ? 'Mettre à jour' : (files.length > 1 ? `Ajouter ${files.length} ressources` : 'Ajouter'))}
          </button>
        </div>
      </form>
    </div>
  );
}

export default ResourceForm;